import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "../styles/AdminDashboard.css";

const API_BASE_URL = "https://growrise.shop";

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getUserId = (user) => {
    return user?.id || user?._id || user?.userId || "";
  };

  const getHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Admin access required. Please login again.",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    localStorage.removeItem("token");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");

    window.location.href = "/login";
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: "GET",
        headers: getHeaders(),
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch users");
      }

      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to load users",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return users;

    return users.filter((user) => {
      return (
        String(user.name || "").toLowerCase().includes(keyword) ||
        String(user.email || "").toLowerCase().includes(keyword) ||
        String(user.referralCode || "").toLowerCase().includes(keyword)
      );
    });
  }, [users, search]);

  const openEditModal = (user) => {
    setSelectedUser({
      ...user,
      balance: String(user.balance ?? 0),
      lockedBalance: String(user.lockedBalance ?? 0),
      totalProfit: String(user.totalProfit ?? 0),
    });
  };

  const closeEditModal = () => {
    setSelectedUser(null);
  };

  const handleSelectedChange = (field, value) => {
    setSelectedUser((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveBalanceChanges = async () => {
    if (!selectedUser) return;

    const userId = getUserId(selectedUser);

    if (!userId) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "User ID not found",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/balance`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({
            balance: Number(selectedUser.balance || 0),
            lockedBalance: Number(selectedUser.lockedBalance || 0),
            totalProfit: Number(selectedUser.totalProfit || 0),
          }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update balance");
      }

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User balance updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      await fetchUsers();
      closeEditModal();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update balance",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateUserStatus = async (userId, isActive) => {
    if (!userId) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "User ID not found",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/status`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({ isActive }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update status");
      }

      await fetchUsers();

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User status updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update status",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const updateUserRole = async (userId, isAdmin) => {
    if (!userId) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "User ID not found",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/role`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({ isAdmin }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update role");
      }

      await fetchUsers();

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User role updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update role",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const updateUserKyc = async (userId, kycStatus) => {
    if (!userId) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "User ID not found",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/kyc`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({ kycStatus }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update KYC status");
      }

      await fetchUsers();

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User KYC updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update KYC status",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  return (
    <div className="admin-dashboard-page">
      <div className="admin-dashboard-wrapper">
        <div className="admin-topbar">
          <div>
            <h1>Admin Dashboard</h1>
            <p>User control and management panel</p>
          </div>

          <button className="admin-refresh-btn" onClick={fetchUsers}>
            Refresh
          </button>
        </div>

        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <span>Total Users</span>
            <strong>{users.length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Active Users</span>
            <strong>{users.filter((user) => user.isActive).length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Admins</span>
            <strong>{users.filter((user) => user.isAdmin).length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Verified KYC</span>
            <strong>
              {users.filter((user) => user.kycStatus === "verified").length}
            </strong>
          </div>
        </div>

        <div className="admin-users-card">
          <div className="admin-card-top">
            <h2>Users</h2>

            <input
              type="text"
              placeholder="Search by name, email, referral code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-search-input"
            />
          </div>

          {loading ? (
            <div className="admin-empty-state">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="admin-empty-state">No users found</div>
          ) : (
            <div className="admin-users-table-wrap">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Balance</th>
                    <th>Locked</th>
                    <th>Profit</th>
                    <th>KYC</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => {
                    const userId = getUserId(user);

                    return (
                      <tr key={userId || user.email}>
                        <td>{user.name || "-"}</td>
                        <td>{user.email || "-"}</td>
                        <td>${Number(user.balance || 0).toFixed(2)}</td>
                        <td>${Number(user.lockedBalance || 0).toFixed(2)}</td>
                        <td>${Number(user.totalProfit || 0).toFixed(2)}</td>

                        <td>
                          <select
                            value={user.kycStatus || "unverified"}
                            onChange={(e) =>
                              updateUserKyc(userId, e.target.value)
                            }
                            className="admin-table-select"
                          >
                            <option value="unverified">unverified</option>
                            <option value="pending">pending</option>
                            <option value="verified">verified</option>
                            <option value="rejected">rejected</option>
                          </select>
                        </td>

                        <td>
                          <button
                            className={`admin-badge-btn ${
                              user.isActive ? "active" : "inactive"
                            }`}
                            onClick={() =>
                              updateUserStatus(userId, !user.isActive)
                            }
                          >
                            {user.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>

                        <td>
                          <button
                            className={`admin-badge-btn ${
                              user.isAdmin ? "admin" : "user"
                            }`}
                            onClick={() =>
                              updateUserRole(userId, !user.isAdmin)
                            }
                          >
                            {user.isAdmin ? "Admin" : "User"}
                          </button>
                        </td>

                        <td>
                          <button
                            className="admin-edit-btn"
                            onClick={() => openEditModal(user)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedUser && (
          <div className="admin-modal-overlay">
            <div className="admin-modal">
              <div className="admin-modal-top">
                <h3>Edit User Balance</h3>
                <button className="admin-close-btn" onClick={closeEditModal}>
                  ×
                </button>
              </div>

              <div className="admin-modal-user">
                <span>{selectedUser.name}</span>
                <strong>{selectedUser.email}</strong>
              </div>

              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Balance</label>
                  <input
                    type="number"
                    value={selectedUser.balance}
                    onChange={(e) =>
                      handleSelectedChange("balance", e.target.value)
                    }
                  />
                </div>

                <div className="admin-form-group">
                  <label>Locked Balance</label>
                  <input
                    type="number"
                    value={selectedUser.lockedBalance}
                    onChange={(e) =>
                      handleSelectedChange("lockedBalance", e.target.value)
                    }
                  />
                </div>

                <div className="admin-form-group">
                  <label>Total Profit</label>
                  <input
                    type="number"
                    value={selectedUser.totalProfit}
                    onChange={(e) =>
                      handleSelectedChange("totalProfit", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="admin-modal-actions">
                <button className="admin-cancel-btn" onClick={closeEditModal}>
                  Cancel
                </button>

                <button
                  className="admin-save-btn"
                  onClick={saveBalanceChanges}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}